/**
 * A saved hero appearance: which outfit variant per slot + which swatch per
 * region. Stored on the account (server side), sent to the party in
 * Welcome/RoomState, rendered as canon part frames + an exact hex→hex LUT
 * (client heroLut — the same pass the pipeline's bake used, proven
 * byte-identical by tools/heropipeline/recolour_gate.py).
 *
 * The sim NEVER sees an appearance: it is presentation, not state. A floor
 * replays from seed + command log with or without looks, and the state hash
 * is identical. The account module (server) validates this shape at write
 * time; the renderer re-validates and degrades gracefully (unknown variant →
 * the slot's default part, unknown swatch id → canon).
 *
 * `outfit` slot names are the hero catalog's outfit slots (the atlas
 * contract) — a single list here so the server's write gate and the
 * creator's picker can never disagree.
 */
import { isValidRegionColours, type RegionColours } from "./palette.js";

/** The hero catalog's outfit slots (atlas `outfit.slots` — pipeline contract). */
export const OUTFIT_SLOTS = ["top", "pants", "hair", "leg", "feet", "torso", "head", "weapon", "shield"] as const;
export type OutfitSlot = (typeof OUTFIT_SLOTS)[number];

export interface HeroAppearance {
  /** slot → variant name (null = unequipped; absent = the slot's default). */
  outfit: Record<string, string | null>;
  /** region → swatch id (0/absent = canon). */
  colours: RegionColours;
}

const MAX_VARIANT_LEN = 24;
const isStrOrNull = (v: unknown): v is string | null =>
  v === null || (typeof v === "string" && v.length > 0 && v.length <= MAX_VARIANT_LEN);

/** Read-time compatibility for appearances saved before footwear had its
 * own slot. Explicit modern feet always wins, independent of property order.
 * The old creator's leg:null meant "no shoes". Armour keeps the leg key. */
export function normalizeOutfitSlots(
  source: Readonly<Record<string, string | null>> | null | undefined,
): Record<string, string | null> {
  const outfit = { ...source };
  const legacy = outfit.leg;
  const base = typeof legacy === "string" ? legacy.replace(/-[0-9a-f]{6}$/, "") : legacy;
  if (base === "boots" || base === "tallboots") {
    if (!Object.hasOwn(outfit, "feet")) outfit.feet = legacy ?? null;
    delete outfit.leg;
  } else if (legacy === null && !Object.hasOwn(outfit, "feet")) {
    outfit.feet = null;
  }
  return outfit;
}

/**
 * Shape validation only — the semantic authority is the renderer (it alone
 * knows the committed atlas + palette). Returns a normalized copy (never
 * the input) or null when the shape is wrong.
 */
export function parseAppearance(v: unknown): HeroAppearance | null {
  if (typeof v !== "object" || v === null) return null;
  const o = v as { outfit?: unknown; colours?: unknown };
  if (o.colours !== undefined && !isValidRegionColours(o.colours)) return null;
  const outfit: Record<string, string | null> = {};
  if (o.outfit !== undefined) {
    if (typeof o.outfit !== "object" || o.outfit === null || Array.isArray(o.outfit)) return null;
    for (const [slot, variant] of Object.entries(o.outfit)) {
      if (!(OUTFIT_SLOTS as readonly string[]).includes(slot)) return null;
      if (variant !== undefined && !isStrOrNull(variant)) return null;
      outfit[slot] = variant ?? null;
    }
  }
  return { outfit: normalizeOutfitSlots(outfit), colours: (o.colours as RegionColours) ?? {} };
}
